import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import canvasRepair from "@/assets/bluebonnet/glamping-tent-canvas-hole-seam-repair.jpg.asset.json";
import roofWallRepair from "@/assets/bluebonnet/glamping-tent-roof-wall-repair.jpg.asset.json";
import glampingTentExterior from "@/assets/bluebonnet/glamping-safari-tent-texas-hill-country.png.asset.json";
import safariGlampingLogo from "@/assets/bluebonnet/texas-made-shade-covers-safari-glamping-logo.png.asset.json";
import glampingTentsHero from "@/assets/bluebonnet/glamping-tents.jpg.asset.json";
import glampingTentInterior from "@/assets/bluebonnet/glamping-tent-interior.jpg.asset.json";

const FAQS = [
  {
    q: "Do you sell pre-made safari tent kits?",
    a: "No. We don't retail off-the-shelf kits. Every safari or glamping tent starts with your idea, your site and your guest experience. We design the layout, develop the cut and fabrication plan, engineer the frame if needed, and build it in our Seguin shop.",
  },
  {
    q: "What kind of glamping tent repairs do you do?",
    a: "Re-sewn roof and wall seams, patched canvas and storm damage, new zippers and zipper flaps, replaced windows and screens, rebuilt door panels, new tie-downs, D-rings and chains, and full canvas recovers on an existing frame.",
  },
  {
    q: "Can you build a custom safari tent for my property?",
    a: "Yes. Bring us your vision, platform size and any sketches. We'll design the footprint, wall height, door and window placement, screens, storm flaps, liners and interior drapes, then fabricate it with marine-grade thread and commercial canvas or vinyl sewn in our Seguin shop.",
  },
  {
    q: "Will canvas hold up to Texas sun and storms?",
    a: "We build with solution-dyed and treated canvas or vinyl rated for UV, mildew and water resistance, and we reinforce the stress points that fail first in Texas — ridge seams, corner pulls and door zippers.",
  },
  {
    q: "Do you add curtains, liners or lighting inside glamping tents?",
    a: "Yes. Interior drapes, room dividers, blackout liners and string or bistro lighting are all things we sew and install, so a rental tent feels finished rather than temporary.",
  },
  {
    q: "Where do you work on safari and glamping tents?",
    a: "San Antonio, Austin, New Braunfels, Seguin, Boerne, the Texas Hill Country and ranch properties across Central and South Texas. Repairs can be shipped or dropped off at our Seguin shop.",
  },
];

export const Route = createFileRoute("/safari-glamping-tents")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Custom Safari & Glamping Tents in Texas — Designed & Fabricated | Texas Made Shade",
      description:
        "Custom safari and glamping tents designed, engineered and fabricated in Texas from your ideas. Canvas seams re-sewn, zippers, windows and full recovers — Rio Grande Series™ hospitality, San Antonio, Austin & the Hill Country.",
      image: safariGlampingLogo.url,
      socialTitle: "Custom Safari & Glamping Tents — Designed & Fabricated in Texas",
      socialDescription:
        "We don't sell kits. We design and fabricate custom canvas safari and glamping tents from your ideas, plus expert tent repairs — sewn in our Seguin shop.",
      keywords:
        "custom safari tents texas, glamping tent designer texas, safari tent fabrication, glamping tents built to order, glamping tent repairs san antonio, canvas wall tent repair texas, custom safari tent builder, glamping tent canvas recover, glamping tent zipper replacement, safari tent austin, glamping tents hill country, glamping safari tent texas hill country",
    }),
    scripts: serviceHeadScripts({
      path: "/safari-glamping-tents",
      serviceName: "Safari & Glamping Tents — Custom Design, Engineering & Repair",
      serviceType: "Custom safari and glamping tent design, engineering, fabrication and repair",
      breadcrumbName: "Safari & Glamping Tents",
      description:
        "Custom safari and glamping tents designed, engineered and fabricated by Texas Made Shade from your ideas — canvas seams, zippers, windows, liners and full recovers, serving San Antonio, Austin and the Texas Hill Country.",
      faqs: FAQS,
    }),
  }),
  component: SafariGlampingTentsPage,
});

function SafariGlampingTentsPage() {
  return (
    <ServicePage
      currentPath="/safari-glamping-tents"
      serviceName="Safari & Glamping Tents"
      breadcrumbs={[
        { label: "Hospitality & Resorts", to: "/rio-grande-series" },
        { label: "Glamping & Safari Tents" },
      ]}
      keywordOutline={[
        { id: "overview", label: "Safari & Glamping Tents in Texas" },
        { id: "why-us", label: "Why Texas Made Shade for Glamping Tents" },
        { id: "details", label: "Custom Builds, Finishes & Series" },
        { id: "applications", label: "Where Our Tents Are Used" },
        { id: "faqs", label: "Safari & Glamping Tent FAQs" },
        { id: "gallery", label: "Safari & Glamping Tent Gallery" },
        { id: "repairs", label: "Glamping & Safari Tent Repairs" },
      ]}
      eyebrow="★ RIO GRANDE SERIES™ · CUSTOM SAFARI & GLAMPING TENTS ★"
      title="Custom Safari & Glamping Tents in Texas — Designed & Fabricated From Your Ideas"
      intro="You bring the idea. We design the tent, engineer the structure if needed, and fabricate it in our Seguin shop. Every safari and glamping tent we build is custom — sized to your deck, built for your guest experience, and sewn to handle Texas sun and storms. We also repair tents you already own, including tents we didn't build."
      heroImage={glampingTentsHero.url}
      heroImageClassName="object-cover"
      whyUsPlacement="afterHero"
      features={[
        "Custom designed from your idea, sketch or site plan",
        "Engineered and fabricated in our Seguin shop",
        "No off-the-shelf kits — every tent is built to order",
        "Canvas, vinyl, liners and drapes sewn in-house",
        "Full glamping tent repairs on tents we didn't build",
        "Rio Grande Series™ hospitality grade",
      ]}
      body={[
        {
          heading: "How we turn your idea into a finished tent",
          text: "We start with your vision — a sketch, a photo, a platform size or just a guest experience you want to create. Our team drafts the layout, selects the frame and fabric system, and confirms the engineering before anything is cut. Then we fabricate the canvas envelope, seams, windows, doors and finishes in-house.",
          bullets: [
            "Consultation and site review — footprint, view and wind direction",
            "Design layout with door, window and screen placement",
            "Frame engineering and fabric specification",
            "Custom fabrication, sewing and finish-out in our shop",
            "Delivery and installation support across Texas",
          ],
          cta: { label: "Start your custom tent design", href: "/estimate-quotes#general" },
        },
        {
          heading: "Built to your footprint, not a catalog size",
          text: "Your property isn't a stock size, and your tent shouldn't be either. We cut and sew each envelope to fit your platform, frame or deck. That means the right wall height, the right door location, and the right details — storm flaps, reinforced ridge seams, corner pulls and tie-downs that match your site.",
          bullets: [
            "Sized to your deck, platform or pad",
            "Door, window and screen placement your way",
            "Storm flaps, liners and interior drapes",
            "Reinforced ridge seams, corner pulls and tie-downs",
          ],
        },
        {
          heading: "Finishing the inside: drapes, liners & lighting",
          text: "Guests judge a glamping tent from the inside. We sew interior drapes, blackout liners and room dividers, and we can run string or bistro lighting through the frame so the tent reads like a room rather than a shelter.",
          bullets: [
            "Interior drapes and privacy dividers",
            "Blackout and insulating liners",
            "String and bistro lighting through the frame",
            "Matching table covers, banners and signage",
          ],
          image: {
            src: glampingTentInterior.url,
            alt: "Interior of a custom safari glamping tent with cream canvas walls, sewn drapes and liners, king bed and warm Edison pendant lighting",
            caption: "Sewn-to-fit drapes, liners and lighting inside a custom safari glamping tent.",
          },
        },
        {
          heading: "Part of the Rio Grande Series™",
          text: "Glamping and safari tents belong to our Rio Grande Series™ — hospitality and resorts. They're a guest-experience product for hotels, resorts, glamping properties, safari camps and retreat venues. Need vendor, market or event tents instead? Those live in the Bluebonnet Series™.",
          bullets: [
            "Hotels, resorts and boutique lodging",
            "Glamping properties and safari camps",
            "Retreats, spas and hospitality venues",
            "Event and vendor tents → Bluebonnet Series™",
          ],
          cta: { label: "Explore the Rio Grande Series™", href: "/rio-grande-series" },
        },
      ]}
      applicationsEyebrow="APPLICATIONS"
      applicationsHeading="Where Our Safari & Glamping Tents Are Used"
      applications={[
        "Glamping resorts & ranch stays",
        "Short-term rental properties",
        "Wedding & event venues",
        "Hunting camps & outfitters",
        "Winery and brewery guest sites",
        "Retreat centers & summer camps",
        "River and lakefront properties",
        "Corporate retreats & pop-up hospitality",
        "Film, photo and production camps",
        "Hill Country vacation properties",
      ]}
      gallerySection={{
        eyebrow: "★ CUSTOM SAFARI & GLAMPING TENT GALLERY ★",
        title: "Texas Hill Country Glamping Tent — Designed & Built to Order",
        intro: "A custom safari-style glamping tent designed for a Texas Hill Country property — shaded deck, string lighting and native landscaping. This is the kind of one-off canvas structure we design, fabricate and finish for guest stays across San Antonio, Austin, New Braunfels, Seguin and the surrounding ranch country.",
        images: [
          {
            src: glampingTentsHero.url,
            alt: "Custom safari glamping lodge tent with a welded aluminum frame, cream canvas roof and a covered front porch on a raised wood deck with lounge chairs and warm string lighting at dusk",
            label: "Custom Safari Lodge Tent · Raised Deck",
            caption: "Designed and fabricated for the site — all-welded aluminum primary frame, replaceable canvas envelope, raised deck and covered porch.",
            category: "Custom Builds",
          },
          {
            src: glampingTentInterior.url,
            alt: "Interior of a custom safari glamping tent with cream canvas walls, sewn drapes and liners, king bed and warm Edison pendant lighting",
            label: "Custom Safari Tent · Interior",
            caption: "Interior finish-out — sewn liners, drapes, gable window and lighting run through the frame.",
            category: "Interiors",
          },
          {
            src: glampingTentExterior.url,
            alt: "Custom safari glamping tent on a Texas Hill Country property with a shaded wooden deck, string lights, native plants and a gravel path leading to the entrance",
            label: "Hill Country Safari Glamping Tent",
            caption: "Custom canvas safari glamping tent with deck, string lighting and native landscaping — designed and built by Texas Made Shade.",
            category: "Custom Builds",
          },
        ],
        categories: ["Custom Builds", "Interiors"],
        cta: {
          headline: "Want a tent like this on your property?",
          subhead: "Bring your idea and we'll design, engineer and fabricate a custom safari or glamping tent for your Texas site.",
          buttonLabel: "Start a Custom Tent Design",
          href: "/estimate-quotes#general",
        },
        relatedLinks: [
          { to: "/rio-grande-series", anchor: "Rio Grande Series™ Hospitality & Resorts", blurb: "Cabanas, glamping and safari tents, spa shade and guest-facing structures for Texas hospitality properties." },
          { to: "/commercial-event-tents", anchor: "Commercial Event Tents & Custom Canopies", blurb: "Pop-up canopies, printed tops, sidewalls and vendor booth tents for markets and events." },
          { to: "/awnings", anchor: "Custom Awnings & Outdoor Shades", blurb: "Matching awnings, shades and outdoor curtains for your glamping site." },
        ],
      }}
      gallerySectionPlacement="default"
      letsTalkImageSrc={glampingTentsHero.url}
      letsTalkImageAlt="Custom safari glamping lodge tent with welded aluminum frame, canvas roof and covered porch on a raised deck at dusk — designed and built by Texas Made Shade"
      children={
        <section id="repairs" className="bg-cream py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <p className="text-sm uppercase tracking-widest text-slate-700 mb-3">★ Glamping &amp; Safari Tent Repairs ★</p>
            <h2 className="font-serif text-3xl md:text-4xl text-navy-deep mb-4">
              Tent Repairs — Holes, Windows, Floating Roofs &amp; Walls
            </h2>
            <p className="text-slate-700 max-w-3xl mb-10">
              Most glamping and safari tents fail in the same few places, and almost all of it is fixable. We repair tents we
              didn't build, and we do the sewing and welding ourselves in our Seguin shop — so you get the tent back in service
              instead of starting over.
            </p>
            <div className="grid gap-6 md:grid-cols-2 mb-10">
              <ul className="space-y-3 text-slate-700">
                <li className="flex gap-3"><span className="text-red">■</span><span><strong className="text-navy-deep">Holes, tears &amp; storm damage</strong> — patched or panels replaced with matching commercial canvas or vinyl and marine-grade thread.</span></li>
                <li className="flex gap-3"><span className="text-red">■</span><span><strong className="text-navy-deep">Fabric windows that no longer function</strong> — clouded or cracked vinyl windows, dead zippers, screens and roll-up flaps rebuilt so they open, close and seal again.</span></li>
                <li className="flex gap-3"><span className="text-red">■</span><span><strong className="text-navy-deep">Floating roof issues</strong> — sagging, pooling water, stretched seams, pulled corners and loose tension corrected and reinforced.</span></li>
              </ul>
              <ul className="space-y-3 text-slate-700">
                <li className="flex gap-3"><span className="text-red">■</span><span><strong className="text-navy-deep">Floating main roof replacement</strong> — a brand-new fly/main roof cut and sewn to your existing frame when the old one is past patching.</span></li>
                <li className="flex gap-3"><span className="text-red">■</span><span><strong className="text-navy-deep">Wall replacement &amp; wall repairs</strong> — single walls, door walls or full sets remade, with new zippers, hems, tie-downs and reinforced stress points.</span></li>
                <li className="flex gap-3"><span className="text-red">■</span><span><strong className="text-navy-deep">Hardware &amp; tie-down rebuilds</strong> — grommets, D-rings, tie chains, webbing and straps restored while the tent is in the shop.</span></li>
              </ul>
            </div>
            <div className="grid gap-6 md:grid-cols-2 mb-10">
              <figure className="border-2 border-navy-deep bg-white">
                <img src={canvasRepair.url} alt="Open seam and torn canvas along a glamping tent roof edge, showing the mesh backing and stitching that has to be re-sewn during a tent repair" className="w-full h-auto" loading="lazy" decoding="async" />
                <figcaption className="p-4 text-sm text-slate-600">A blown roof-edge seam and torn canvas — re-sewn and reinforced rather than replaced.</figcaption>
              </figure>
              <figure className="border-2 border-navy-deep bg-white">
                <img src={roofWallRepair.url} alt="Inside a canvas glamping tent looking up at the floating main roof, ridge and wall panel above a framed doorway with a wall light" className="w-full h-auto" loading="lazy" decoding="async" />
                <figcaption className="p-4 text-sm text-slate-600">Floating main roof, ridge and wall panels — replaced or repaired to fit your existing frame.</figcaption>
              </figure>
            </div>
            <a href="/estimate-quotes#general" className="inline-block bg-red text-white font-semibold px-7 py-3 transition hover:bg-red/90">
              Send us your tent repair
            </a>
          </div>
        </section>
      }
      faqs={FAQS}
    />
  );
}
